import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import {
  Send, Users, Mail, CheckCircle, AlertCircle,
  FileText, Sparkles, X, Clock, RefreshCw, Timer
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AiModelSelect, useAiModel } from "../../components/admin/AiModelSelect";

const Campaigns = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [followUpStats, setFollowUpStats] = useState(null);
  const [activeTab, setActiveTab] = useState("followups");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiModel, setAiModel] = useAiModel();
  
  const [formData, setFormData] = useState({
    template_id: "",
    subject: "",
    content: "",
    recipient_type: "all",
    recipient_ids: []
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [templatesRes, campaignsRes, leadsRes, fuRes, fuStatsRes] = await Promise.all([
        axios.get(`${API}/templates`, { headers }),
        axios.get(`${API}/campaigns`, { headers }),
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/follow-ups`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/follow-ups/stats`, { headers }).catch(() => ({ data: null }))
      ]);
      setTemplates(templatesRes.data);
      setCampaigns(campaignsRes.data);
      setLeads(leadsRes.data);
      setFollowUps(fuRes.data);
      setFollowUpStats(fuStatsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processFollowUps = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/follow-ups/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = response.data;
      toast({
        title: d.sent > 0 ? `Sent ${d.sent} follow-up emails` : "No follow-ups due yet",
        description: d.processed > 0 ? `Processed ${d.processed} queued emails` : "All follow-ups are up to date"
      });
      fetchData();
    } catch {
      toast({ title: "Failed to process follow-ups", variant: "destructive" });
    }
    setProcessing(false);
  };

  const loadTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        template_id: templateId,
        subject: template.subject,
        content: template.content
      });
    }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate`, {
        prompt_type: "email_draft",
        model: aiModel,
        context: {
          topic: formData.subject || "Weekly specials",
          audience: formData.recipient_type
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, content: response.data.content });
      toast({ title: "Content generated!" });
    } catch {
      toast({ title: "AI generation failed", variant: "destructive" });
    }
    setGenerating(false);
  };

  const sendCampaign = async () => {
    if (!formData.subject || !formData.content) {
      toast({ title: "Please fill in subject and content", variant: "destructive" });
      return;
    }

    setSending(true);
    try {
      const payload = {
        subject: formData.subject,
        content: formData.content,
        recipient_type: formData.recipient_type === "all" ? null : formData.recipient_type,
        recipient_ids: formData.recipient_type === "selected" ? formData.recipient_ids : []
      };

      const response = await axios.post(`${API}/campaigns/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: response.data.message });
      fetchData();
      setShowCompose(false);
      setFormData({ template_id: "", subject: "", content: "", recipient_type: "all", recipient_ids: [] });
    } catch (error) {
      toast({ 
        title: "Failed to send campaign", 
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive" 
      });
    }
    setSending(false);
  };

  const getRecipientCount = () => {
    if (formData.recipient_type === "all") return leads.length;
    if (formData.recipient_type === "selected") return formData.recipient_ids.length;
    return leads.filter(l => l.lead_type === formData.recipient_type).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="campaigns-title">
            Email Campaigns
          </h1>
          <p className="text-slate-600">Send emails to your leads</p>
        </div>
        <button
          onClick={() => setShowCompose(true)}
          className="btn-primary flex items-center"
          data-testid="compose-campaign-btn"
        >
          <Send className="w-5 h-5 mr-2" /> Compose Campaign
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Recipients</p>
              <p className="text-2xl font-bold text-[#0A2342]">{leads.length}</p>
            </div>
            <Users className="w-10 h-10 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Campaigns Sent</p>
              <p className="text-2xl font-bold text-[#0A2342]">{campaigns.length}</p>
            </div>
            <Mail className="w-10 h-10 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Templates Available</p>
              <p className="text-2xl font-bold text-[#0A2342]">{templates.length}</p>
            </div>
            <FileText className="w-10 h-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Tab switcher — Follow-Up Campaigns first (primary operational view) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="campaigns-tabs">
        <button
          onClick={() => setActiveTab("followups")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === "followups" ? "bg-[#0A2342] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-followups"
        >
          Follow-Up Campaigns{followUpStats ? ` · ${followUpStats.scheduled || 0} scheduled` : ""}
        </button>
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === "all" ? "bg-[#0A2342] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-all-campaigns"
        >
          All Campaigns{campaigns.length ? ` · ${campaigns.length}` : ""}
        </button>
      </div>

      {/* Campaign History */}
      {activeTab === "all" && (
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[#0A2342]">Campaign History</h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No campaigns sent yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="p-6 hover:bg-slate-50" data-testid={`campaign-${campaign.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-[#0A2342]">{campaign.subject}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Sent to {campaign.sent_count} of {campaign.recipients_count} recipients
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {campaign.status === "sent" ? (
                      <span className="badge bg-green-100 text-green-700 flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" /> Sent
                      </span>
                    ) : (
                      <span className="badge bg-red-100 text-red-700 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" /> {campaign.status}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {new Date(campaign.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Follow-Up Campaigns */}
      {activeTab === "followups" && (
      <div className="bg-white rounded-xl border border-slate-200" data-testid="follow-up-section">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#0A2342]">Follow-Up Campaigns</h2>
            <p className="text-sm text-slate-500 mt-1">4-email drip: Resources → Specials (Day 2) → Budget Picks (Day 5) → Tips (Day 10)</p>
          </div>
          <button
            onClick={processFollowUps}
            disabled={processing}
            className="btn-primary flex items-center text-sm"
            data-testid="process-followups-btn"
          >
            {processing ? (
              <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Processing...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Send Due Follow-Ups</>
            )}
          </button>
        </div>

        {/* Follow-Up Stats */}
        {followUpStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-b border-slate-100">
            {[
              { label: "Total", value: followUpStats.total, color: "text-slate-600" },
              { label: "Scheduled", value: followUpStats.scheduled, color: "text-amber-600" },
              { label: "Sent", value: followUpStats.sent, color: "text-green-600" },
              { label: "Failed", value: followUpStats.failed, color: "text-red-600" }
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {followUps.length === 0 ? (
          <div className="p-12 text-center">
            <Timer className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No follow-ups scheduled yet</p>
            <p className="text-xs text-slate-400 mt-1">Follow-ups are auto-created when users download the resource bundle</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {followUps.map((fu) => (
              <div key={fu.id} className="p-4 px-6 flex items-center justify-between hover:bg-slate-50" data-testid={`followup-${fu.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#0A2342]">{fu.email}</p>
                  <p className="text-xs text-slate-400">
                    {fu.name && <span>{fu.name} &bull; </span>}
                    {fu.subject && <span className="text-slate-500">{fu.subject} &bull; </span>}
                    {new Date(fu.scheduled_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {fu.type && (
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-medium text-slate-500">
                      {fu.type.replace("drip_", "").replace("_", " ")}
                    </span>
                  )}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    fu.status === "scheduled" ? "bg-amber-100 text-amber-700" :
                    fu.status === "sent" ? "bg-green-100 text-green-700" :
                    "bg-red-100 text-red-700"
                  }`} data-testid={`followup-status-${fu.id}`}>
                    {fu.status === "scheduled" && <Clock className="w-3 h-3 inline mr-1" />}
                    {fu.status === "sent" && <CheckCircle className="w-3 h-3 inline mr-1" />}
                    {fu.status === "failed" && <AlertCircle className="w-3 h-3 inline mr-1" />}
                    {fu.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            data-testid="compose-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2342] font-['Playfair_Display']">
                Compose Campaign
              </h2>
              <button
                onClick={() => setShowCompose(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Template Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Load from Template (Optional)
                </label>
                <select
                  value={formData.template_id}
                  onChange={(e) => loadTemplate(e.target.value)}
                  className="input-field"
                  data-testid="campaign-template-select"
                >
                  <option value="">Start from scratch</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Recipients */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Recipients
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { value: "all", label: "All Leads" },
                    { value: "renter", label: "Renters" },
                    { value: "buyer", label: "Buyers" },
                    { value: "investor", label: "Investors" }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, recipient_type: option.value })}
                      className={`py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                        formData.recipient_type === option.value
                          ? "bg-[#0A2342] text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`recipient-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Will send to <strong>{getRecipientCount()}</strong> recipients
                </p>
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="input-field"
                  placeholder="What they aren't telling you about Houston..."
                  data-testid="campaign-subject"
                />
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">Email Content</label>
                  <div className="flex items-center gap-3">
                  <AiModelSelect value={aiModel} onChange={setAiModel} />
                  <button
                    onClick={generateWithAI}
                    disabled={generating}
                    className="text-sm text-[#DE6E4B] hover:text-[#0A2342] flex items-center"
                    data-testid="campaign-ai-generate"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {generating ? "Generating..." : "Generate with AI"}
                  </button>
                  </div>
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="textarea-field font-mono text-sm"
                  rows={12}
                  placeholder="Hey [Name],

Your email content here..."
                  data-testid="campaign-content"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Use [Name] to personalize with recipient's name
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={sendCampaign}
                  disabled={sending || !formData.subject || !formData.content}
                  className="btn-primary flex items-center"
                  data-testid="campaign-send-btn"
                >
                  {sending ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Send Campaign
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowCompose(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
